import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Compass, History, Loader2, Target, X } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import useRetryFetch from "../../hooks/useRetryFetch";
import { useMmText } from "../../hooks/useMmText";
import {
  getBiography,
  writebackConfirm,
  type MmEvent,
  type MmObject,
  type MmRegister,
  type MmVisitReason,
} from "../../api/mentalModels";
import MmBiography from "./MmBiography";
import MmVoiceSwitch from "./MmVoiceSwitch";
import MmVisit, { type MmVisitEditor, type MmVisitGoal, type MmWriteback } from "./MmVisit";
import { MmAreaChips, MmConnected } from "./MmModelContext";
import { MmTypeTile, mmMetaParts, mmTypeName } from "./MmRow";

const REASONS: Array<{ key: string; fb: string; reason: MmVisitReason }> = [
  { key: "reason_friction_external", fb: "Something happened", reason: { kind: "friction", frictionType: "external" } },
  { key: "reason_friction_internal", fb: "Something rose up in me", reason: { kind: "friction", frictionType: "internal" } },
  { key: "reason_plan", fb: "I'm working a plan", reason: { kind: "plan" } },
];

export interface MmInitialVisit {
  goal?: MmVisitGoal;
  /** true: land on the inline "what brings you here?" first (staged handoffs). */
  ask: boolean;
}

interface Props {
  object: MmObject;
  register: MmRegister;
  onRegisterChange: (r: MmRegister) => void;
  /** Land on step 3 (a staged path / capture's "start working on it"). */
  initialVisit?: MmInitialVisit;
  /** The page took initialVisit — the host should not hand it over again. */
  onInitialVisitUsed?: () => void;
  /** Open another model in place (Connected chips). */
  onOpenModel: (id: string) => void;
  /** Something about the model changed (visit ended, areas) — refresh lists. */
  onChanged?: () => void;
  onSpacesChange?: (spaceIds: string[]) => void;
}

type Stage = "idle" | "ask" | "visit";

/**
 * THE place a mental model opens (spec 2026-11-03 model page §A): header with
 * the person's words once, Connected, the next step (reason → visit inline)
 * and quiet links (History, goal, path).
 */
const MmModelPage: React.FC<Props> = ({
  object,
  register,
  onRegisterChange,
  initialVisit,
  onInitialVisitUsed,
  onOpenModel,
  onChanged,
  onSpacesChange,
}) => {
  const { t, i18n } = useTranslation();
  const { tm } = useMmText(register);
  const retryFetch = useRetryFetch();
  const language = i18n.language?.split("-")[0] || "en";

  const [live, setLive] = useState<MmObject>(object);
  useEffect(() => setLive(object), [object]);

  const [stage, setStage] = useState<Stage>(
    initialVisit ? (initialVisit.ask ? "ask" : "visit") : "idle"
  );
  const [goal, setGoal] = useState<MmVisitGoal | undefined>(initialVisit?.goal);
  const [reason, setReason] = useState<MmVisitReason | undefined>(undefined);
  const [editor, setEditor] = useState<MmVisitEditor>(null);
  // Remounts the visit for each new "Spend a few minutes"
  const [visitKey, setVisitKey] = useState(0);
  const [writeback, setWriteback] = useState<MmWriteback | null>(null);

  const [history, setHistory] = useState<{
    events: MmEvent[];
    series: Record<string, Array<{ t: string; v: number }>>;
  } | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const stepRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!initialVisit) return;
    onInitialVisitUsed?.();
    stepRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startVisit = (r: MmVisitReason | undefined) => {
    setReason(r);
    setWriteback(null);
    setVisitKey((k) => k + 1);
    setStage("visit");
  };

  /** Quiet links: in a visit they open its editor; otherwise ask first. */
  const openEditor = (e: Exclude<MmVisitEditor, null>) => {
    setEditor(e);
    if (stage === "idle") setStage("ask");
  };

  const endVisit = ({ writeback: wb }: { writeback?: MmWriteback }) => {
    setStage("idle");
    setEditor(null);
    setGoal(undefined);
    setWriteback(wb ?? null);
    setHistory(null);
    onChanged?.();
  };

  const resolveWriteback = async (decision: "accepted" | "declined") => {
    if (!writeback) return;
    try {
      await writebackConfirm(retryFetch, writeback.objectId, {
        decision,
        proposed: decision === "accepted" ? writeback.proposed : undefined,
      });
    } catch (e) {
      console.error(e);
    }
    setWriteback(null);
  };

  const toggleHistory = async () => {
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    setHistoryOpen(true);
    if (history) return;
    setHistoryLoading(true);
    try {
      const res = await getBiography(retryFetch, live._id);
      setHistory({ events: res.events, series: res.series });
    } catch (e) {
      console.error(e);
    } finally {
      setHistoryLoading(false);
    }
  };

  const words = live.workingStatement || live.statement;
  const resolution = live.profile?.resolution;
  const clarity = typeof resolution === "number" ? Math.max(0, Math.min(5, Math.round(resolution))) : null;

  const quietLink =
    "inline-flex items-center gap-1.5 rounded-lg px-2 min-h-[44px] text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

  return (
    <article
      aria-label={mmTypeName(t, live.objectType)}
      className="max-w-[640px] space-y-6 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-200"
    >
      {/* 1 · Header */}
      <header className="space-y-3">
        <div className="flex items-center gap-2">
          <MmTypeTile type={live.objectType} />
          <span className="text-sm font-medium text-foreground/80">{mmTypeName(t, live.objectType)}</span>
        </div>

        {live.label && live.label !== words && (
          <p dir="auto" className="text-xs font-semibold uppercase tracking-wide text-primary/70">
            {live.label}
          </p>
        )}
        <blockquote
          dir="auto"
          data-testid="mm-page-words"
          className="whitespace-pre-wrap text-lg sm:text-xl leading-relaxed text-foreground border-s-4 border-primary/30 ps-4"
        >
          {words}
        </blockquote>

        <p className="text-xs text-muted-foreground" data-testid="mm-page-meta">
          {mmMetaParts(t, live, language, true).join(" · ")}
        </p>

        <MmAreaChips objectId={live._id} register={register} onSpacesChange={onSpacesChange} />

        {clarity !== null && (
          <div
            className="flex items-center gap-2 text-xs text-muted-foreground"
            role="img"
            aria-label={tm("page_clarity_aria", "How clear it is: {{n}} of 5").replace("{{n}}", String(clarity))}
          >
            <span>{tm("page_clarity", "How clear it is")}</span>
            <span className="flex gap-1" aria-hidden>
              {[1, 2, 3, 4, 5].map((i) => (
                <span
                  key={i}
                  className={cn("h-2 w-2 rounded-full", i <= clarity ? "bg-primary" : "bg-border")}
                />
              ))}
            </span>
          </div>
        )}
      </header>

      {/* 2 · Connected */}
      <MmConnected
        objectId={live._id}
        register={register}
        statements={[live.statement, live.workingStatement]}
        onOpenModel={onOpenModel}
      />

      {/* 3 · Next step */}
      <section
        ref={stepRef}
        aria-label={tm("page_next_step", "Next step")}
        className="rounded-2xl border border-border bg-card p-4 sm:p-5 space-y-4"
      >
        {writeback && (
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
            <p className="text-sm font-medium mb-2">
              {tm("writeback_title", "Your thinking moved — update the source item?")}
            </p>
            <div className="text-xs space-y-1 mb-3">
              <p className="text-muted-foreground line-through">{writeback.current}</p>
              <p className="text-foreground">{writeback.proposed}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => resolveWriteback("accepted")}>
                {tm("writeback_accept", "Update it")}
              </Button>
              <Button size="sm" variant="outline" onClick={() => resolveWriteback("declined")}>
                {tm("writeback_decline", "Keep the original")}
              </Button>
            </div>
          </div>
        )}

        {stage === "idle" && (
          <Button size="lg" className="w-full sm:w-auto min-h-[44px]" onClick={() => setStage("ask")}>
            {tm("page_spend_time", "Spend a few minutes with it")}
          </Button>
        )}

        {stage === "ask" && (
          <div
            role="group"
            aria-label={tm("reason_title", "What brings you here?")}
            className="space-y-2 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-2 motion-safe:duration-200"
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold">{tm("reason_title", "What brings you here?")}</p>
                <p className="text-xs text-muted-foreground">
                  {tm("reason_subtitle", "Knowing why helps pick the right moves.")}
                </p>
              </div>
              <button
                type="button"
                aria-label={t("cancel", "Cancel")}
                onClick={() => {
                  setStage("idle");
                  setEditor(null);
                }}
                className="inline-flex items-center justify-center h-11 w-11 -m-2 rounded-xl text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {REASONS.map(({ key, fb, reason: r }) => (
              <button
                key={key}
                type="button"
                onClick={() => startVisit(r)}
                className="w-full rounded-2xl border border-border px-4 py-2.5 min-h-[44px] text-sm text-start hover:border-primary/50 hover:bg-primary/5 transition-colors motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                {tm(key, fb)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => startVisit(undefined)}
              className="w-full text-center text-xs text-muted-foreground hover:text-foreground min-h-[44px]"
            >
              {tm("reason_skip", "Just open it")}
            </button>
          </div>
        )}

        {stage === "visit" && (
          <MmVisit
            key={visitKey}
            object={live}
            register={register}
            reason={reason}
            goal={goal}
            editor={editor}
            onEditorChange={setEditor}
            onObjectChange={setLive}
            onEnd={endVisit}
          />
        )}
      </section>

      {/* Outside the step card: a setting, not part of the step. */}
      <MmVoiceSwitch register={register} onChange={onRegisterChange} className="-mt-2 px-1" />

      {/* History (biography), inline so a running visit stays put */}
      {historyOpen && (
        <section aria-label={tm("history", "History")} className="space-y-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {tm("history", "History")}
          </h3>
          {historyLoading || !history ? (
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          ) : (
            <MmBiography events={history.events} series={history.series} />
          )}
        </section>
      )}

      {/* 4 · Quiet links */}
      <nav aria-label={tm("page_more", "More")} className="flex flex-wrap items-center gap-x-2 -ms-2">
        <button type="button" onClick={toggleHistory} aria-expanded={historyOpen} className={quietLink}>
          <History className="w-4 h-4" /> {tm("history", "History")}
        </button>
        <button type="button" onClick={() => openEditor("goal")} className={quietLink}>
          <Target className="w-4 h-4" /> {tm("set_goal", "Set a goal for this thought")}
        </button>
        <button type="button" onClick={() => openEditor("path")} className={quietLink}>
          <Compass className="w-4 h-4" /> {tm("pick_path", "Follow a path")}
        </button>
      </nav>
    </article>
  );
};

export default MmModelPage;
